import { countRawStyleLeaks } from "../../cogo-kg-browser/src/exemplar-quality-core.js"

export type CogoDesignCompilerStatus = "pass" | "partial" | "blocked"

export type CogoDesignCompilerSource = "className" | "designClass" | "style"

export type CogoDesignFactKind =
  | "component"
  | "layout"
  | "spacing"
  | "surface"
  | "typography"
  | "radius"
  | "shadow"
  | "responsive"
  | "style-authority"

export interface CogoDesignFact {
  kind: CogoDesignFactKind
  source: CogoDesignCompilerSource
  path: string
  target: string
  value: string
}

export interface CogoDesignCompilerReport {
  schemaVersion: "cogo-design-compiler-report.v1"
  status: CogoDesignCompilerStatus
  facts: CogoDesignFact[]
  blockedReasons: string[]
  warnings: string[]
  authorStyleObjectCount: number
  convertedStyleObjectCount: number
  unconvertedStyleObjectCount: number
  normalizedUtilityCount: number
  remainingUnparsedClassCount: number
}

export interface CogoDesignCompilerResult {
  blockJson: Record<string, unknown>
  report: CogoDesignCompilerReport
}

const RESPONSIVE_PREFIXES = new Set(["sm", "md", "lg", "xl", "2xl"])
const ALIGN_VALUES = new Set(["start", "center", "end", "stretch", "baseline"])
const JUSTIFY_VALUES = new Set(["start", "center", "end", "between", "around", "evenly"])
const DENSITY_VALUES = new Set(["compact", "default", "comfortable"])
const SURFACE_VALUES = new Set(["canvas", "card", "component", "muted", "panel", "popover"])
const RADIUS_VALUES = new Set(["none", "sm", "md", "lg", "xl", "2xl", "full"])
const SHADOW_VALUES = new Set(["none", "sm", "md", "lg", "xl"])
const STYLE_KEYS = new Set([
  "alignItems",
  "aspectRatio",
  "background",
  "backgroundColor",
  "borderColor",
  "borderRadius",
  "borderWidth",
  "boxShadow",
  "color",
  "columnGap",
  "display",
  "flexDirection",
  "gap",
  "gridTemplateColumns",
  "height",
  "justifyContent",
  "margin",
  "maxHeight",
  "maxWidth",
  "minHeight",
  "minWidth",
  "padding",
  "paddingBlock",
  "paddingInline",
  "rowGap",
  "width",
])

const COLOR_STYLE_KEYS = new Set(["background", "backgroundColor", "borderColor", "color"])

/**
 * Normalize Cogo authoring-time design shorthands into typed design facts.
 *
 * Important boundary: renderers must not depend on parsing arbitrary class/style strings at runtime.
 * This compiler is the ingestion/generation-side normalization layer. It reports any author-authored
 * `style` object so promotion gates can reject it, and it removes fully understood style objects from
 * the compiled clone to keep the durable block JSON renderer-light.
 */
export function compileCogoDesignContract(blockJson: Record<string, unknown>): CogoDesignCompilerResult {
  const compiled = cloneRecord(blockJson)
  const report: CogoDesignCompilerReport = {
    schemaVersion: "cogo-design-compiler-report.v1",
    status: "pass",
    facts: [],
    blockedReasons: [],
    warnings: [],
    authorStyleObjectCount: 0,
    convertedStyleObjectCount: 0,
    unconvertedStyleObjectCount: 0,
    normalizedUtilityCount: 0,
    remainingUnparsedClassCount: 0,
  }

  const root =
    readRecord(readRecord(compiled.ui)?.root) ??
    readRecord(compiled.root) ??
    (typeof compiled.type === "string" ? compiled : null)
  if (root) visitNode(root, readRecord(readRecord(compiled.ui)?.root) ? "ui.root" : "root", report)

  if (report.blockedReasons.length > 0 || report.unconvertedStyleObjectCount > 0) {
    report.status = "blocked"
  } else if (
    report.authorStyleObjectCount > 0 ||
    report.remainingUnparsedClassCount > 0 ||
    report.warnings.length > 0
  ) {
    report.status = "partial"
  } else {
    report.status = "pass"
  }

  return { blockJson: compiled, report }
}

function visitNode(node: Record<string, unknown>, path: string, report: CogoDesignCompilerReport): void {
  collectClassFacts(node, "designClass", path, report)
  collectClassFacts(node, "className", path, report)
  normalizeStyleObject(node, path, report)

  const children = node.children
  if (Array.isArray(children)) {
    children.forEach((child, index) => {
      const childRecord = readRecord(child)
      if (childRecord) visitNode(childRecord, `${path}.children[${index}]`, report)
    })
  }
}

function collectClassFacts(
  node: Record<string, unknown>,
  key: "className" | "designClass",
  path: string,
  report: CogoDesignCompilerReport,
): void {
  const value = node[key]
  if (typeof value !== "string" || value.trim().length === 0) return

  for (const token of value.trim().split(/\s+/)) {
    if (isUnsafeClassToken(token)) {
      report.blockedReasons.push(`unsafe-design-class:${path}.${key}:${token}`)
      continue
    }

    const fact = factFromClassToken(token, key, path)
    if (fact) {
      report.facts.push(fact)
      report.normalizedUtilityCount += 1
      applyClassTokenToNode(node, token)
    } else {
      report.remainingUnparsedClassCount += 1
      report.warnings.push(`unparsed-design-class:${path}.${key}:${token}`)
    }
  }
}

function factFromClassToken(
  token: string,
  source: "className" | "designClass",
  path: string,
): CogoDesignFact | null {
  if (token.startsWith("@ds:")) {
    return { kind: "component", source, path, target: token, value: token }
  }

  const { prefix, core } = splitResponsivePrefix(token)
  const responsiveFact = prefix
    ? { kind: "responsive" as const, source, path, target: `uiox:breakpoint-${prefix}`, value: prefix }
    : null

  const kind = classifyUtility(core)
  if (!kind) return responsiveFact

  const fact: CogoDesignFact = {
    kind,
    source,
    path,
    target: `uiox:${kind}-${core}`,
    value: token,
  }
  return responsiveFact
    ? {
        ...fact,
        target: `${responsiveFact.target}/${fact.target}`,
      }
    : fact
}

function normalizeStyleObject(
  node: Record<string, unknown>,
  path: string,
  report: CogoDesignCompilerReport,
): void {
  const style = readRecord(node.style)
  if (!style) return

  report.authorStyleObjectCount += 1
  const unsupported: string[] = []
  const convertedEntries: Array<[string, unknown]> = []

  for (const [key, value] of Object.entries(style)) {
    if (!STYLE_KEYS.has(key)) {
      unsupported.push(key)
      continue
    }
    if (isUnsafeStyleValue(key, value)) {
      unsupported.push(`${key}:unsafe`)
      continue
    }
    report.facts.push({
      kind: factKindForStyleKey(key),
      source: "style",
      path,
      target: `uiox:${factKindForStyleKey(key)}-${kebab(key)}`,
      value: String(value),
    })
    convertedEntries.push([key, value])
  }

  if (unsupported.length > 0) {
    report.unconvertedStyleObjectCount += 1
    report.blockedReasons.push(`unconverted-style-object:${path}:${unsupported.join(",")}`)
    return
  }

  for (const [key, value] of convertedEntries) applyStyleEntryToNode(node, key, value)
  delete node.style
  report.convertedStyleObjectCount += 1
  report.warnings.push(`converted-author-style-object:${path}`)
}

function applyClassTokenToNode(node: Record<string, unknown>, token: string): void {
  if (token.startsWith("@ds:")) {
    applyDesignHandleToNode(node, token)
    return
  }

  const { prefix, core } = splitResponsivePrefix(token)
  if (core === "grid") {
    setLayoutValue(node, "kind", "grid", prefix)
    return
  }
  if (core === "flex") {
    setLayoutValue(node, "kind", "row", prefix)
    return
  }
  if (core === "inline-flex") {
    setLayoutValue(node, "kind", "inline", prefix)
    return
  }
  if (core === "flex-col") {
    setLayoutValue(node, "kind", "stack", prefix)
    return
  }
  if (core === "flex-row") {
    setLayoutValue(node, "kind", "row", prefix)
    return
  }

  const gridCols = /^grid-cols-(\d+)$/.exec(core)
  if (gridCols) {
    setLayoutValue(node, "columns", Number(gridCols[1]), prefix)
    return
  }

  const gap = /^gap-(\d+(?:\.\d+)?)$/.exec(core)
  if (gap) {
    setLayoutValue(node, "gap", Number(gap[1]), prefix)
    return
  }

  const align = /^items-(start|center|end|stretch|baseline)$/.exec(core)
  if (align) {
    setLayoutValue(node, "align", align[1], prefix)
    return
  }

  const justify = /^justify-(start|center|end|between|around|evenly)$/.exec(core)
  if (justify) {
    setLayoutValue(node, "justify", justify[1], prefix)
    return
  }

  const surface = surfaceFromClass(core)
  if (surface) {
    setDesignValue(node, "surface", surface)
    return
  }

  const tone = toneFromClass(core)
  if (tone) {
    setDesignValue(node, "tone", tone)
    return
  }

  const radius = /^rounded(?:-(none|sm|md|lg|xl|2xl|full))?$/.exec(core)
  if (radius) {
    setDesignValue(node, "radius", radius[1] ?? "md")
    return
  }

  const shadow = /^shadow(?:-(none|sm|md|lg|xl))?$/.exec(core)
  if (shadow) {
    setDesignValue(node, "shadow", shadow[1] ?? "md")
    return
  }

  const style = /^style-([a-z][a-z0-9-]*)$/.exec(core)
  if (style) {
    setDesignValue(node, "style", style[1])
  }
}

function applyDesignHandleToNode(node: Record<string, unknown>, token: string): void {
  const match = /^@ds:([a-z-]+)\.([a-z0-9-]+)$/.exec(token)
  if (!match) return
  const [, kind, value] = match
  if (kind === "layout") {
    setLayoutValue(node, "kind", value)
  } else if (kind === "surface" && SURFACE_VALUES.has(value)) {
    setDesignValue(node, "surface", value)
  } else if (kind === "density" && DENSITY_VALUES.has(value)) {
    setDesignValue(node, "density", value)
  } else if (kind === "radius" && RADIUS_VALUES.has(value)) {
    setDesignValue(node, "radius", value)
  } else if (kind === "shadow" && SHADOW_VALUES.has(value)) {
    setDesignValue(node, "shadow", value)
  } else if (kind === "style" && isSafeStyleScope(value)) {
    setDesignValue(node, "style", value)
  } else if (kind === "component") {
    setDesignValue(node, "component", value)
  } else if (kind === "state") {
    setDesignValue(node, "state", value)
  }
}

function applyStyleEntryToNode(node: Record<string, unknown>, key: string, value: unknown): void {
  if (key === "display") {
    if (value === "grid") setLayoutValue(node, "kind", "grid")
    if (value === "flex") setLayoutValue(node, "kind", "row")
    if (value === "inline-flex") setLayoutValue(node, "kind", "inline")
    return
  }
  if (key === "flexDirection") {
    if (value === "column") setLayoutValue(node, "kind", "stack")
    if (value === "row") setLayoutValue(node, "kind", "row")
    return
  }
  if (key === "gap" || key === "columnGap" || key === "rowGap") {
    const spacing = styleLengthToSpacingToken(value)
    if (spacing != null) setLayoutValue(node, "gap", spacing)
    return
  }
  if (key === "gridTemplateColumns") {
    const columns = columnsFromTemplate(value)
    if (columns != null) setLayoutValue(node, "columns", columns)
    return
  }
  if (key === "alignItems") {
    if (typeof value === "string" && ALIGN_VALUES.has(value)) setLayoutValue(node, "align", value)
    return
  }
  if (key === "justifyContent") {
    if (typeof value === "string" && JUSTIFY_VALUES.has(value)) setLayoutValue(node, "justify", value)
    return
  }
  if (key === "padding" || key === "paddingBlock" || key === "paddingInline") {
    const spacing = styleLengthToSpacingToken(value)
    if (spacing != null) node.padding = spacing
    return
  }
  if (key === "background" || key === "backgroundColor") {
    const surface = surfaceFromStyleValue(value)
    if (surface) setDesignValue(node, "surface", surface)
    return
  }
  if (key === "color") {
    const tone = toneFromStyleValue(value)
    if (tone) setDesignValue(node, "tone", tone)
    return
  }
  if (key === "borderRadius") {
    const radius = radiusFromStyleValue(value)
    if (radius) setDesignValue(node, "radius", radius)
    return
  }
  if (key === "boxShadow") {
    const shadow = shadowFromStyleValue(value)
    if (shadow) setDesignValue(node, "shadow", shadow)
  }
}

function setLayoutValue(
  node: Record<string, unknown>,
  key: string,
  value: unknown,
  breakpoint?: string,
): void {
  const layout = ensureRecord(node, "layout")
  if (!breakpoint) {
    layout[key] = value
    if (key === "kind") {
      if (value === "grid") layout.type = "grid"
      if (value === "stack") {
        layout.type = "flex"
        layout.direction = "column"
      }
      if (value === "row" || value === "inline") {
        layout.type = "flex"
        layout.direction = "row"
      }
    }
    return
  }
  const responsive = ensureRecord(layout, "responsive")
  const overrides = ensureRecord(responsive, breakpoint)
  overrides[key] = value
  if (key === "kind" && value === "grid") overrides.type = "grid"
}

function setDesignValue(node: Record<string, unknown>, key: string, value: unknown): void {
  ensureRecord(node, "design")[key] = value
}

function surfaceFromClass(token: string): string | null {
  if (token === "bg-card") return "card"
  if (token === "bg-muted") return "muted"
  if (token === "bg-background") return "canvas"
  if (token === "bg-popover") return "popover"
  if (token === "bg-primary" || token === "bg-secondary" || token === "bg-accent") return "component"
  return null
}

function toneFromClass(token: string): string | null {
  if (token === "text-primary") return "primary"
  if (token === "text-secondary") return "secondary"
  if (token === "text-destructive") return "destructive"
  if (token === "text-muted-foreground") return "muted"
  if (token === "text-foreground") return "default"
  return null
}

function surfaceFromStyleValue(value: unknown): string | null {
  if (value === "ctok:card" || value === "var(--card)") return "card"
  if (value === "ctok:muted" || value === "var(--muted)") return "muted"
  if (value === "ctok:background" || value === "var(--background)") return "canvas"
  if (value === "ctok:popover" || value === "var(--popover)") return "popover"
  return null
}

function toneFromStyleValue(value: unknown): string | null {
  if (value === "ctok:primary" || value === "var(--primary)") return "primary"
  if (value === "ctok:secondary" || value === "var(--secondary)") return "secondary"
  if (value === "ctok:destructive" || value === "var(--destructive)") return "destructive"
  if (value === "ctok:muted-foreground" || value === "var(--muted-foreground)") return "muted"
  if (value === "ctok:foreground" || value === "var(--foreground)") return "default"
  return null
}

function radiusFromStyleValue(value: unknown): string | null {
  if (typeof value !== "string") return null
  const match = /^(?:ctok:radius-|radius-|rounded-)(none|sm|md|lg|xl|2xl|full)$/.exec(value)
  return match?.[1] ?? null
}

function shadowFromStyleValue(value: unknown): string | null {
  if (typeof value !== "string") return null
  const match = /^(?:ctok:shadow-|shadow-)(none|sm|md|lg|xl)$/.exec(value)
  return match?.[1] ?? null
}

function columnsFromTemplate(value: unknown): number | null {
  if (typeof value !== "string") return null
  const repeat = /^repeat\((\d+),/.exec(value)
  if (repeat) return Number(repeat[1])
  const parts = value.trim().split(/\s+/).filter(Boolean)
  return parts.length > 0 ? parts.length : null
}

function styleLengthToSpacingToken(value: unknown): number | string | null {
  if (typeof value === "number" && Number.isFinite(value)) return pxToSpacing(value)
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  const px = /^(\d+(?:\.\d+)?)px$/.exec(trimmed)
  if (px) return pxToSpacing(Number(px[1]))
  const rem = /^(\d+(?:\.\d+)?)rem$/.exec(trimmed)
  if (rem) return Number(rem[1]) * 4
  if (/^\d+(?:\.\d+)?$/.test(trimmed)) return trimmed
  return null
}

function pxToSpacing(value: number): number {
  const scaled = value / 4
  return Number.isInteger(scaled) ? scaled : Number(scaled.toFixed(3))
}

function splitResponsivePrefix(token: string): { prefix?: string; core: string } {
  const parts = token.split(":")
  if (parts.length <= 1) return { core: token }
  const prefix = parts.find((part) => RESPONSIVE_PREFIXES.has(part))
  return { prefix, core: parts[parts.length - 1] ?? token }
}

function classifyUtility(token: string): CogoDesignFactKind | null {
  if (
    token === "flex" ||
    token === "grid" ||
    token === "block" ||
    token === "inline-flex" ||
    token === "hidden" ||
    token === "relative" ||
    token === "absolute" ||
    token === "sticky" ||
    token === "fixed" ||
    startsWithAny(token, [
      "aspect-", "auto-cols-", "auto-rows-", "col-span-", "content-", "flex-", "grid-cols-",
      "grid-rows-", "h-", "items-", "justify-", "max-h-", "max-w-", "min-h-", "min-w-",
      "order-", "overflow-", "place-", "row-span-", "self-", "size-", "w-",
    ])
  ) {
    return "layout"
  }
  if (
    startsWithAny(token, [
      "gap-", "gap-x-", "gap-y-", "m-", "mb-", "ml-", "mr-", "mt-", "mx-", "my-",
      "p-", "pb-", "pl-", "pr-", "pt-", "px-", "py-", "space-x-", "space-y-",
    ])
  ) {
    return "spacing"
  }
  if (
    startsWithAny(token, [
      "bg-", "border-", "fill-", "outline-", "ring-", "stroke-", "text-foreground",
      "text-muted-foreground", "text-primary", "text-secondary", "text-destructive",
    ])
  ) {
    return "surface"
  }
  if (
    token === "truncate" ||
    startsWithAny(token, ["font-", "leading-", "line-clamp-", "text-", "tracking-"])
  ) {
    return "typography"
  }
  if (startsWithAny(token, ["rounded"])) return "radius"
  if (startsWithAny(token, ["shadow"])) return "shadow"
  if (startsWithAny(token, ["style-"])) return "style-authority"
  return null
}

function factKindForStyleKey(key: string): CogoDesignFactKind {
  if (["gap", "columnGap", "rowGap", "padding", "paddingBlock", "paddingInline", "margin"].includes(key)) {
    return "spacing"
  }
  if (["background", "backgroundColor", "borderColor", "borderWidth", "color"].includes(key)) {
    return "surface"
  }
  if (key === "borderRadius") return "radius"
  if (key === "boxShadow") return "shadow"
  return "layout"
}

function isUnsafeClassToken(token: string): boolean {
  return countRawStyleLeaks(token) > 0 || /\boklch\(/.test(token)
}

function isUnsafeStyleValue(key: string, value: unknown): boolean {
  if (typeof value !== "string") return false
  if (COLOR_STYLE_KEYS.has(key) && /#[0-9a-fA-F]{3,8}\b|rgba?\(|\boklch\(/.test(value)) return true
  if (key === "boxShadow" && value.trim().length > 0 && !/^ctok:|^uiox:|^shadow-/.test(value)) return true
  return countRawStyleLeaks(value) > 0
}

function startsWithAny(value: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => value.startsWith(prefix))
}

function kebab(value: string): string {
  return value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
}

function isSafeStyleScope(value: string): boolean {
  return /^[a-z][a-z0-9-]{0,48}$/.test(value)
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function ensureRecord(parent: Record<string, unknown>, key: string): Record<string, unknown> {
  const current = readRecord(parent[key])
  if (current) return current
  const next: Record<string, unknown> = {}
  parent[key] = next
  return next
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>
}
